import { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  useTheme,
  useMediaQuery,
  Stack,
} from '@mui/material';
import {
  ArrowForwardIos,
  ArrowBackIos,
} from '@mui/icons-material';
import PropertyCard, { PropertyType } from './PropertyCard';

type PropertyCarouselProps = {
  title: string;
  description?: string;
  properties: PropertyType[];
  viewAllLink?: string;
  variant?: 'standard' | 'featured';
};

const PropertyCarousel = ({
  title,
  description,
  properties,
  viewAllLink,
  variant = 'standard',
}: PropertyCarouselProps) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const isSm = useMediaQuery(theme.breakpoints.down('md'));
  const isMd = useMediaQuery(theme.breakpoints.down('lg'));
  
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const [itemsPerView, setItemsPerView] = useState(4);
  const [itemWidth, setItemWidth] = useState(300);
  const [containerWidth, setContainerWidth] = useState(0);
  const [maxIndex, setMaxIndex] = useState(0);

  useEffect(() => {
    const updateDimensions = () => {
      // Determine items per view based on screen size
      if (isXs) {
        setItemsPerView(1);
      } else if (isSm) {
        setItemsPerView(2);
      } else if (isMd) {
        setItemsPerView(3);
      } else {
        setItemsPerView(4);
      }

      if (containerRef.current) {
        const newContainerWidth = containerRef.current.offsetWidth;
        setContainerWidth(newContainerWidth);
        setItemWidth(newContainerWidth / itemsPerView);
      }
    };

    updateDimensions();
    
    // Recalculate maximum index based on properties length and items per view
    setMaxIndex(Math.max(0, properties.length - itemsPerView));

    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, [isXs, isSm, isMd, properties.length, itemsPerView]);

  const handlePrev = () => {
    setActiveIndex((current) => Math.max(current - 1, 0));
  };

  const handleNext = () => {
    setActiveIndex((current) => Math.min(current + 1, maxIndex));
  };

  const slideStyle = {
    transform: `translateX(-${activeIndex * itemWidth}px)`,
    transition: 'transform 0.5s ease-in-out',
    display: 'flex',
    width: `${properties.length * itemWidth}px`,
  };
  
  // Reset active index when properties array changes
  useEffect(() => {
    setActiveIndex(0);
  }, [properties]);

  const isFeatured = variant === 'featured';

  return (
    <Box sx={{ my: 6 }}>
      {/* Header */}
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          mb: 3,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h4" component="h2" fontWeight={700} gutterBottom>
            {title}
          </Typography>
          {description && (
            <Typography variant="body1" color="text.secondary">
              {description}
            </Typography>
          )}
        </Box>
        
        {viewAllLink && (
          <Button 
            variant="outlined" 
            color="primary" 
            component="a" 
            href={viewAllLink}
          >
            View All
          </Button>
        )}
      </Box>

      {/* Carousel */}
      <Box sx={{ position: 'relative' }}>
        {/* Left arrow */}
        <IconButton
          onClick={handlePrev}
          disabled={activeIndex === 0}
          sx={{
            position: 'absolute',
            left: -16,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 2,
            backgroundColor: theme.palette.background.paper,
            boxShadow: 2,
            '&:hover': {
              backgroundColor: theme.palette.background.paper,
            },
            opacity: activeIndex === 0 ? 0.5 : 1,
          }}
          aria-label="previous properties"
        >
          <ArrowBackIos fontSize="small" />
        </IconButton>

        {/* Carousel container */}
        <Box
          sx={{
            width: '100%',
            overflow: 'hidden',
            position: 'relative',
          }}
          ref={containerRef}
        >
          <Box sx={slideStyle}>
            {properties.map((property, index) => (
              <Box
                key={property.id}
                sx={{
                  flexShrink: 0,
                  width: `${itemWidth}px`,
                  padding: 1,
                }}
              >
                <PropertyCard 
                  property={property} 
                  variant={isFeatured ? 'featured' : 'standard'} 
                />
              </Box>
            ))}
          </Box>
        </Box>

        {/* Right arrow */}
        <IconButton
          onClick={handleNext}
          disabled={activeIndex >= maxIndex}
          sx={{
            position: 'absolute',
            right: -16,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 2,
            backgroundColor: theme.palette.background.paper,
            boxShadow: 2,
            '&:hover': {
              backgroundColor: theme.palette.background.paper,
            },
            opacity: activeIndex >= maxIndex ? 0.5 : 1,
          }}
          aria-label="next properties"
        >
          <ArrowForwardIos fontSize="small" />
        </IconButton>
      </Box>

      {/* Pagination dots */}
      <Stack 
        direction="row" 
        spacing={1} 
        justifyContent="center" 
        sx={{ mt: 3 }}
      >
        {Array.from({ length: maxIndex + 1 }).map((_, index) => (
          <Box
            key={index}
            onClick={() => setActiveIndex(index)}
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: activeIndex === index ? 'primary.main' : 'grey.300',
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': {
                bgcolor: activeIndex === index ? 'primary.dark' : 'grey.400',
              },
            }}
          />
        ))}
      </Stack>
    </Box>
  );
};

export default PropertyCarousel;